import { useEffect, useState } from "react";
import { OrderSummary } from "../../../types/Order/orderSummary";
import {
  useLayerService,
  useOrderService,
  useUserAdminService,
} from "../../../hooks";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import { Pagination } from "../../../types/PaginiationResult";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { FilterType } from "../../../types/ApiTable/ColumnFilter";
import { OrderListParams } from "../../../types/Order/OrderListParams";
import { getUserDetails } from "../../auth/AuthProvider";
import { Roles } from "../../../constant/Roles";
import OrderTableDropdown from "./OrderTableDropdown";
import OrderDetailsModal from "../../modal/Order/OrderDetailsModal";
import Price from "../../../@ui/Price";
import { Stats } from "../../../types/Order/Stats";
import { Card, ListGroup } from "react-bootstrap";
import ToggleSwitch from "../../../@ui/ToggleSwitch";
import AssignOrderModal from "./AssignOrderModel";

interface ExtendedTableState {
  state: ApiTableState;
  isPremium: boolean;
  excludeTraccOrders: boolean;
  excludeBasemapOrders: boolean;
}

const Orders = () => {
  const orderService = useOrderService();
  const userAdminService = useUserAdminService();
  const layerService = useLayerService();
  const isAdmin = getUserDetails()?.roles.includes(Roles.ADMINISTRATOR);
  const token = getUserDetails()?.token;

  const [stats, setStats] = useState<Stats | undefined>();
  const [orders, setOrders] = useState<Pagination<OrderSummary[]>>({
    records: [],
    totalPages: 1,
    page: 1,
  });
  const [extendedState, setExtendedState] = useState<ExtendedTableState>({
    state: {
      pagination: {
        page: 1,
      },
    },
    isPremium: true,
    excludeTraccOrders: true,
    excludeBasemapOrders: true,
  });
  const [layerOptions, setLayerOptions] = useState<
    {
      label: string;
      value: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | undefined>();
  const [assignOrderId, setAssignOrderId] = useState<string | undefined>();

  useEffect(() => {
    const { state, isPremium, excludeTraccOrders, excludeBasemapOrders } =
      extendedState;

    const filterExpression = getCombinedFilterExpression(
      state.filterExpression ?? "",
      isPremium,
      excludeTraccOrders,
      excludeBasemapOrders,
    );
    const sortExpression = state.sort
      ? `${state.sort.field} ${state.sort.direction}`
      : undefined;

    const params: OrderListParams = {
      page: state.pagination.page,
      pageSize: state.pagination.pageSize,
      filterExpression: filterExpression,
      sortExpression: sortExpression,
    };

    const abortController = new AbortController();
    setLoading(true);
    orderService
      .listOrders(params, abortController)
      .then((r) => {
        setOrders(r);
        setLoading(false);
      })
      .catch(() => {});

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendedState]);

  useEffect(() => {
    orderService
      .Stats()
      .then((stats) => setStats(stats))
      .catch(() => {});

    layerService
      .ListAll()
      .then((layers) => {
        setLayerOptions(
          layers.map((layer) => {
            return {
              label: layer.displayName,
              value: layer.id,
            };
          }),
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStateChange = (tableState: ApiTableState) => {
    setExtendedState({ ...extendedState, state: tableState });
  };

  const isPremiumChange = (isPremium: boolean) => {
    const updatedState = extendedState;
    updatedState.isPremium = isPremium;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const excludeTraccOrdersChange = (excludeTraccOrders: boolean) => {
    const updatedState = extendedState;
    updatedState.excludeTraccOrders = excludeTraccOrders;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const excludeBasemapOrdersChange = (excludeBasemapOrders: boolean) => {
    const updatedState = extendedState;
    updatedState.excludeBasemapOrders = excludeBasemapOrders;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const getCombinedFilterExpression = (
    filter: string | undefined,
    isPremium: boolean,
    excludeTraccOrders: boolean,
    excludeBasemapOrders: boolean,
  ) => {
    let combinedFilter = "";
    const premiumFilter = "isPremium eq true";
    const excludeTractFilter = "UserAgent <> TRACC";
    const excludeBasemapFilter = "OrganisationId <> 0";
    if (filter) combinedFilter = filter;
    if (isPremium)
      combinedFilter =
        combinedFilter === ""
          ? premiumFilter
          : `${combinedFilter} _AND ${premiumFilter}`;

    if (excludeTraccOrders)
      combinedFilter =
        combinedFilter === ""
          ? excludeTractFilter
          : `${combinedFilter} _AND ${excludeTractFilter}`;

    if (excludeBasemapOrders)
      combinedFilter =
        combinedFilter === ""
          ? excludeBasemapFilter
          : `${combinedFilter} _AND ${excludeBasemapFilter}`;

    return combinedFilter;
  };

  const cols = [
    {
      field: "fullName",
      label: "Name",
      renderContents: (value: OrderSummary) => (
        <>{`${value.user.firstName} ${value.user.lastName}`}</>
      ),
      filter: {
        type: FilterType.TYPEAHEAD,
        filterKey: "userId",
        typeaheadOptions: {
          optionLabelKey: "fullName",
          optionValueKey: "userId",
          onSearch: (searchTerm: string) =>
            userAdminService.searchUsers(searchTerm),
        },
      },
      style: { width: "20%" },
      sortable: true,
    },
    {
      field: "organisationId",
      label: "Organisation",
      renderContents: (value: OrderSummary) => <>{value.organisationName}</>,
      style: { width: "20%" },
      hide: !isAdmin,
      filter: {
        type: FilterType.TYPEAHEAD,
        filterKey: "organisationId",
        typeaheadOptions: {
          optionLabelKey: "name",
          optionValueKey: "id",
          onSearch: (searchTerm: string) =>
            userAdminService.searchOrganisations(searchTerm),
        },
      },
    },
    {
      field: "createdDate",
      label: "Created",
      renderContents: (value: OrderSummary) => (
        <>{value.createdDate.toFormat("dd/LL/yyyy, HH:mm:ss")}</>
      ),
      filter: {
        type: FilterType.DATE_RANGE,
        filterKey: "createdDate",
      },
      sortable: true,
      style: { width: "20%" },
    },
    {
      field: "productIds",
      label: "Products",
      renderContents: (value: OrderSummary) => (
        <div style={{ maxWidth: "300px" }} className="d-block text-truncate">
          {value.productNames.sort().join(", ")}
        </div>
      ),
      filter: {
        type: FilterType.SELECT,
        selectOptions: {
          options: layerOptions,
        },
      },
      style: { width: "30%" },
    },
    {
      field: "totalLineItems",
      label: "No. of items",
      renderContents: (value: OrderSummary) => <>{value.totalLineItems}</>,
      style: { width: "10%" },
    },
    {
      field: "totalExTax",
      label: "Value",
      sortable: true,
      renderContents: (value: OrderSummary) => (
        <div className="w-100 text-end">
          <Price
            currencyCode={value.transactionCurrencyCode}
            price={value.totalIncTax}
          />
        </div>
      ),
      style: { width: "1%" },
    },
    {
      field: "",
      label: "",
      style: { width: "1%" },
      renderContents: (value: OrderSummary) => (
        <OrderTableDropdown
          viewInvoice={viewInvoice}
          continueOrder={continueOrder}
          viewDetails={setSelectedOrderId}
          assignOrder={setAssignOrderId}
          value={value}
        />
      ),
    },
  ] as ApiTableColumn[];

  const viewInvoice = (invoiceUrl: string) => {
    window.open(invoiceUrl, "_blank");
  };

  const continueOrder = (orderId: string) => {
    const base = process.env.REACT_APP_SSO_PAYMENT;
    const clientId = process.env.REACT_APP_CLIENT_ID;
    const url = `${base}?orderId=${orderId}&clientId=${clientId}&token=${token}`;
    window.open(url, "_blank");
  };

  const handleAssignToCustomer = (userId: string) => {
    if (!assignOrderId) return;

    orderService.AssignOrder(assignOrderId, userId).then(() => {
      setAssignOrderId(undefined);

      // Reset the table
      const updatedState = extendedState;
      updatedState.state.pagination.page = 1;
      setExtendedState({ ...updatedState });
    });
  };

  return (
    <>
      {stats && (
        <>
          <h5>Order Stats</h5>
          <Card className="mb-2">
            <ListGroup horizontal={true}>
              <ListGroup.Item className="text-center flex-fill">
                All Time
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesAllTime} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersAllTime}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Last Year
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesLastYear} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersLastYear}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Year to Date
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesYTD} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersYTD}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Last Month
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesLastMonth} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersLastMonth}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Month To Date
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesMTD} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersMTD}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Yesterday
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesYesterday} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersYesterday}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="text-center flex-fill">
                Today
                <div className="font-weight-bold" style={{ fontSize: "1.5rem" }}>
                  <Price price={stats.salesToday} currencyCode="GBP" />
                </div>
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "1.2rem" }}>
                  Orders: {stats.premiumOrdersToday}
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </>
      )}

      <ApiTable
        state={extendedState.state}
        data={orders}
        columns={cols}
        pageOptions={[10, 20, 50]}
        onChange={onStateChange}
        loading={loading}
        controls={
          <>
            <ToggleSwitch
              id="premiumOnly"
              label="Show Premium Order Only"
              value={extendedState.isPremium}
              onChange={isPremiumChange}
            />
            <ToggleSwitch
              id="excludeTracc"
              label="Exclude Tracc Orders"
              value={extendedState.excludeTraccOrders}
              onChange={excludeTraccOrdersChange}
            />
            <ToggleSwitch
              id="excludeBasemap"
              label="Exclude Basemap Orders"
              value={extendedState.excludeBasemapOrders}
              onChange={excludeBasemapOrdersChange}
            />
          </>
        }
      />
      {selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(undefined)}
        />
      )}
      {assignOrderId && (
        <AssignOrderModal
          orderId={assignOrderId}
          onAssign={handleAssignToCustomer}
          onClose={() => setAssignOrderId(undefined)}
        />
      )}
    </>
  );
};

export default Orders;
